import React, { useMemo } from 'react';
import Button from '../Button';
import {
  mileageMessage as m, recentTrips, summaryYears, withUnit, yearSummary,
} from '../../domain/mileage';
import { useDateFormat, useLocale, useMonthFormat } from '../../context/LocaleContext';
import s from './Mileage.module.css';

// The Mileage Log's overview (#521), as MMS draws it:
//
//   Recent Trips          up to five cards, newest first: the date, the
//                         destination, `12.5 mi traveled`, and a
//                         `Repeating Trip` tag or, on a one-time trip, a
//                         `Repeat Trip` button
//   Annual summary for 2026 ▾          200 Miles traveled
//   + Add Mileage
//   Month ↓ | Number Of Trips | Total Distance (mi) | Total Duration (HH:MM) | ›
//
// a row a month, a series' future dates in the year included; a month opens
// its own page (`MileageMonth`). The folds are core/src/mileage.rs's
// (`recent_trips`, `year_summary`, `summary_years`); this draws them.
//
// Knowingly short of MMS: its month table has Columns ▾ and Sort ▾ as well;
// this table has four columns and one order, and the month page has both
// menus.
//
// A card's distance is in the trip's own unit and the year's in the studio's,
// converted once a trip, as the report converts them: a Region changed since a
// trip was logged keeps the trip's unit (#521).
export default function MileageOverview({
  trips, unit, today, year, onYear, onMonth, onAdd, onRepeat,
}) {
  const shortDate = useDateFormat();
  const monthName = useMonthFormat();
  const { shownDecimal } = useLocale();
  const recent = useMemo(() => recentTrips(trips, unit, today), [trips, unit, today]);
  const years = useMemo(() => summaryYears(trips, today), [trips, today]);
  const summary = useMemo(() => yearSummary(trips, unit, year, today), [trips, unit, year, today]);

  return (
    <>
      {recent.length ? (
        <section className={s.recent} aria-label={m('recentTrips')}>
          <div className={s.sectionTitle}>{m('recentTrips')}</div>
          <div className={s.cards}>
            {recent.map((r) => (
              <div key={r.id} className={s.card} data-testid="mileage-recent-trip">
                <div className={s.cardDate}>{shortDate(r.occurredOn)}</div>
                <div className={s.cardDestination}>{r.destination || '–'}</div>
                <div className={s.cardDistance}>{shownDecimal(r.traveled)}</div>
                <div className={s.cardFoot}>
                  {r.series ? (
                    <span className={s.tag}>{m('recurringTag')}</span>
                  ) : (
                    <Button variant="secondary" size="sm" onClick={() => onRepeat(r.trip)}>{m('repeatTrip')}</Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <div className={s.summary}>
        <label className={s.summaryTitle}>
          <span>{m('annualSummary')}</span>
          <select className={s.yearSelect} value={year} onChange={(e) => onYear(e.target.value)}>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
        <div className={s.summaryTotal}>{shownDecimal(summary.total)}</div>
      </div>

      <div className={s.toolbar}>
        <Button onClick={onAdd}>{m('addMileage')}</Button>
      </div>

      <div className={s.panel}>
        <table className={s.table}>
          <thead>
            <tr>
              <th>{m('columnMonth')} ↓</th>
              <th>{m('columnTrips')}</th>
              <th>{withUnit(m('columnTotalDistance'), unit)}</th>
              <th>{m('columnTotalDuration')}</th>
              <th aria-label="Open" />
            </tr>
          </thead>
          <tbody>
            {summary.months.map((row) => (
              <tr key={row.month} className={s.clickable} onClick={() => onMonth(row.month)}>
                <td>
                  <button type="button" className={s.linkButton} onClick={(e) => { e.stopPropagation(); onMonth(row.month); }}>
                    {monthName(row.month)}
                  </button>
                </td>
                <td className={s.number}>{row.trips}</td>
                <td className={s.number}>{shownDecimal(row.distance)}</td>
                <td className={s.number}>{row.duration}</td>
                <td className={s.chevron} aria-hidden="true">›</td>
              </tr>
            ))}
            {summary.months.length ? null : (
              <tr><td colSpan={5} className={s.none}>–</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
